import { useState } from 'react';
import { Percent } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TaxRow, useSetTaxRateMutation, useTaxesQuery } from '@/client/api/payment/finance';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { EmptyRow, ErrorNote, FinancePage, card, td, th } from './shell';

const TaxesPage = () => {
  // Rate changes are super-admin only; the server enforces it.
  const { isSuperAdmin } = useHasPermission('payment.manage');
  const { data, isLoading, error } = useTaxesQuery();
  const mutation = useSetTaxRateMutation();
  const [edit, setEdit] = useState<TaxRow | null>(null);
  const [rate, setRate] = useState('');
  const [reason, setReason] = useState('');

  const save = async () => {
    if (!edit) return;
    if (!reason.trim()) {
      toast.error('A reason is required', { richColors: true });
      return;
    }
    try {
      await mutation.mutateAsync({ taxid: edit.taxid, rate: Number(rate), reason: reason.trim() });
      toast.success('Tax rate updated', { richColors: true });
      setEdit(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save', { richColors: true });
    }
  };

  return (
    <FinancePage
      icon={Percent}
      title="Taxes"
      description="The rates the tax engine adds to invoices. A change applies to every invoice issued from about five minutes later; invoices already issued keep the tax they were issued with. Only super admins can change a rate."
    >
      <ErrorNote error={error} />
      <div className={card}>
        <table className="min-w-full divide-y divide-gray-100">
          <thead>
            <tr className="bg-gray-50">
              {['Tax', 'Country', 'Rate', 'Notes', ''].map((h, i) => (
                <th key={i} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {!isLoading && !data?.taxes?.length && <EmptyRow cols={5}>No taxes defined</EmptyRow>}
            {data?.taxes?.map((t) => (
              <tr key={t.taxid}>
                <td className={td}>
                  <span className="font-medium text-gray-900">{t.name}</span>
                  <span className="block font-mono text-xs text-gray-500">taxid {t.taxid}</span>
                </td>
                <td className={td}>{t.country_iso2 || '—'}</td>
                <td className={`${td} font-semibold tabular-nums`}>
                  {t.computed ? 'Computed' : t.rate != null ? `${t.rate}%` : '—'}
                </td>
                <td className={`${td} max-w-md text-gray-500`}>{t.rate_description || '—'}</td>
                <td className={td}>
                  {isSuperAdmin && !t.computed && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEdit(t);
                          setRate(String(t.rate ?? ''));
                          setReason('');
                        }}
                      >
                        Change rate
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>Change rate: {edit.name}</DialogTitle>
                <DialogDescription>
                  Currently {edit.rate}%. The new rate is charged on every invoice issued from about
                  five minutes after you save, across the whole platform.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tax-rate">Rate (%)</Label>
                  <Input
                    id="tax-rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tax-reason">Reason</Label>
                  <Input
                    id="tax-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="For example: GRA rate change effective 1 January"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEdit(null)}>
                  Cancel
                </Button>
                <Button type="button" disabled={mutation.isPending} onClick={save}>
                  Change rate
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </FinancePage>
  );
};

export default TaxesPage;
